import React from 'react';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import CashMovementsPanel from '../../components/finance/cashBank/CashMovementsPanel';
import CashCountsPanel from '../../components/finance/cashBank/CashCountsPanel';
import ReconciliationPanel from '../../components/finance/cashBank/ReconciliationPanel';

type TabKey = 'movements' | 'counts' | 'reconcile';

/**
 * Finance → Cash & bank: the movements on every cash and bank account, the
 * cashier's day-close counts, and bank reconciliation. `?tab=` deep-links.
 */
const CashBankPage: React.FC = () => {
  const { canRead } = useModulePermissions();
  const [params, setParams] = useSearchParams();
  const tabs: Array<{ value: TabKey; label: string; hint: string }> = [
    { value: 'movements', label: 'Movements', hint: "Every rupee in and out of the school's cash and bank accounts, straight from the books." },
    ...(canRead('cash_count') ? [{ value: 'counts' as const, label: 'Cash counts', hint: 'Count each till at day end against what the books say it holds.' }] : []),
    ...(canRead('bank_reconciliation') ? [{ value: 'reconcile' as const, label: 'Reconcile', hint: 'Match each bank account to its statement, line by line.' }] : []),
  ];
  const tab = tabs.find(t => t.value === params.get('tab')) ?? tabs[0];
  const select = (value: TabKey) => { const next = new URLSearchParams(params); next.set('tab', value); setParams(next, { replace: true }); };

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4" fontWeight={700}>Cash &amp; bank</Typography>
        <Typography variant="body2" color="text.secondary">{tab.hint}</Typography>
      </Box>
      {tabs.length > 1 && (
        <Tabs value={tab.value} onChange={(_, v) => select(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
          {tabs.map(t => <Tab key={t.value} value={t.value} label={t.label} sx={{ textTransform: 'none', fontWeight: 600 }} />)}
        </Tabs>
      )}
      {tab.value === 'movements' && <CashMovementsPanel />}
      {tab.value === 'counts' && <CashCountsPanel />}
      {tab.value === 'reconcile' && <ReconciliationPanel />}
    </Box>
  );
};

export default CashBankPage;
